// SGNL-179 — shared PDF export helper.
//
// Renders a Bounteous-branded print-ready document in a popup window,
// then triggers window.print(). Used by:
//   - views/weekly_call.ts (Progress tab, SGNL-177)
//   - views/reports.ts (Reports tab, SGNL-179)
//
// Why a popup + window.print() rather than a PDF library:
//   - No new bundle dependency (~0KB added vs ~300KB for pdfmake/jspdf)
//   - The browser's native print stack handles fonts + page breaks
//   - The user can preview, then pick "Save as PDF" or send to a printer
//   - Recipients get a self-contained HTML doc, no Signal session needed
//
// Security model (4-angle review on PR #113):
//
//   * The popup is opened with `window.open('', '_blank')` — same-origin
//     about:blank. We construct its contents entirely via DOM APIs
//     (createElement + textContent + appendChild) — no `document.write`,
//     no `innerHTML` on untrusted text. SEC-01.
//
//   * The popup carries a CSP meta tag set FIRST so it's active before
//     any other content lands:
//         default-src 'none';
//         style-src 'unsafe-inline';
//         img-src data:;
//         font-src 'none';
//         connect-src 'none';
//         script-src 'none';
//     `'unsafe-inline'` on style-src is required because the brand
//     stylesheet is embedded as an inline <style> block (there is no
//     server-side render step to mint a nonce; the popup is ephemeral).
//     The `default-src 'none'` baseline + explicit `script-src 'none'`
//     prevents script execution regardless of the inline-style allowance,
//     and `img-src data:` closes the CSS-url() exfiltration gap that
//     style-src alone would leave open. ARCH-04 / SEC-04.
//
//   * The cloned tab DOM has its event-handler attributes (`on*`) and
//     `style="…url(…)"` attributes scrubbed before insertion. The CSP
//     above already blocks script execution; this scrub is defence-in-
//     depth so a future CSP weakening doesn't open a script-execution
//     hole on its own. SEC-02.
//
//   * `win.opener` is nullified after the popup is constructed so a
//     compromised popup can't reach back into the SPA's window. SEC-03.
//
//   * Filter chips and titles are HTML-escaped via `esc()` even though
//     they ultimately set `textContent` (which is opaque to HTML) — the
//     redundancy is intentional. If a future refactor moves the chip
//     rendering from textContent to innerHTML, the escape still holds.
//
// All script-locking + DOM-construction is exercised by
// `pdf_export.test.ts`.

import { esc } from "./format";

export interface PdfExportOptions {
  /** The DOM subtree to clone into the popup. Buttons / inputs / selects
   * inside this tree are auto-stripped before injection. */
  tabEl: HTMLElement;
  /** Short string chips rendered in the dark header, one per active scope
   * filter (e.g. ["BU: TechBU", "Owner: prem.kalyan", "Close date scope: 2026"]).
   * Empty array → no .pdf-filters wrapper is rendered. */
  filterChips: string[];
  /** Used for the popup window title attribute (download default name). */
  filename: string;
  /** Big title in the dark header (e.g. "Progress Report"). */
  title: string;
  /** Logo eyebrow above the title. Defaults to "Bounteous · Signal". */
  logo?: string;
  /** Optional callback invoked when window.open returns null OR the
   * popup's document.write/append throws (e.g. SecurityError). The
   * caller typically renders an inline toast. */
  onPopupBlocked?: () => void;
  /** Selectors of elements to strip from the cloned tab before injection.
   * Defaults to interactive controls only. Callers pass additional
   * view-specific selectors (e.g. the Progress picker control) — the
   * generic helper deliberately does NOT know about specific views.
   * ARCH-06. */
  stripSelectors?: string[];
}

const DEFAULT_STRIP = ["button", "input", "select"];

/**
 * Open a print-ready popup and trigger `window.print()`. The popup is
 * locked down via CSP and DOM is built via createElement APIs (no
 * `document.write`, no `innerHTML` on untrusted text). Returns the
 * popup window reference (or `null` when blocked / write failed) so
 * tests can assert against it.
 */
export function exportTabAsPdf(opts: PdfExportOptions): Window | null {
  const {
    tabEl,
    filterChips,
    filename,
    title,
    logo = "Bounteous · Signal",
    onPopupBlocked,
    stripSelectors = [],
  } = opts;
  const win = window.open("", "_blank");
  if (!win) {
    onPopupBlocked?.();
    return null;
  }
  try {
    _buildPopupDocument(win, { filename, title, logo, filterChips });
    // SEC-03: nullify opener so a hypothetical compromised popup can't
    // reach back into the SPA window via window.opener.location etc.
    try {
      (win as unknown as { opener: unknown }).opener = null;
    } catch {
      // Some browsers reject the assignment; that's fine — the CSP
      // above already prevents script execution in the popup.
    }
    _appendStrippedClone(win, tabEl, [...DEFAULT_STRIP, ...stripSelectors]);
    // Q-06: deterministic print trigger — wait for fonts to settle, then
    // one paint, before opening the print dialog. SEC-06: skip if the
    // popup was closed in the interval.
    const triggerPrint = (): void => {
      if (win.closed) return;
      win.requestAnimationFrame(() => {
        if (!win.closed) win.print();
      });
    };
    const fontsReady = (win.document as Document & { fonts?: { ready: Promise<unknown> } })
      .fonts?.ready;
    if (fontsReady) {
      Promise.race([
        fontsReady,
        new Promise((r) => win.setTimeout(r, 1000)),
      ]).then(triggerPrint, triggerPrint);
    } else {
      win.setTimeout(triggerPrint, 300);
    }
    return win;
  } catch {
    // TEST-03: document.write / append can throw (cross-origin extension,
    // popup closed mid-write). Surface as popup-blocked from the caller's
    // POV — same UX as a hard block.
    try {
      win.close();
    } catch {
      /* ignore — popup already gone */
    }
    onPopupBlocked?.();
    return null;
  }
}

// ── internal helpers ─────────────────────────────────────────────────

interface PopupShellOpts {
  filename: string;
  title: string;
  logo: string;
  filterChips: string[];
}

/** Build the popup shell (CSP meta + style + branded header + body
 * container) via DOM APIs only. The CSP meta is appended FIRST so it
 * activates before any subsequent content lands. */
function _buildPopupDocument(win: Window, opts: PopupShellOpts): void {
  const d = win.document;
  d.title = opts.filename;
  // CSP first — must be in <head> before any other content for it to
  // apply to the rest of the document. PR-Agent (PRA-1, PR #113):
  // some Document constructors (createHTMLDocument, certain browsers'
  // about:blank popups) pre-populate a <title> element. `prepend`
  // guarantees the CSP meta lands ahead of whatever the parser already
  // emitted; `appendChild` would put it after.
  const csp = d.createElement("meta");
  csp.setAttribute("http-equiv", "Content-Security-Policy");
  csp.setAttribute(
    "content",
    [
      "default-src 'none'",
      "style-src 'unsafe-inline'",
      "img-src data:",
      "font-src 'none'",
      "connect-src 'none'",
      "script-src 'none'",
    ].join("; "),
  );
  d.head.prepend(csp);
  const charset = d.createElement("meta");
  charset.setAttribute("charset", "UTF-8");
  d.head.appendChild(charset);
  const style = d.createElement("style");
  style.textContent = _STYLE_BLOCK;
  d.head.appendChild(style);

  // Branded header.
  const header = d.createElement("div");
  header.className = "pdf-header";
  header.setAttribute("data-testid", "pdf-header");
  const headerTop = d.createElement("div");
  headerTop.className = "pdf-header-top";
  const headerInner = d.createElement("div");
  headerInner.setAttribute("data-testid", "pdf-header-inner");
  const logoEl = d.createElement("div");
  logoEl.className = "pdf-logo";
  // esc() then assign to textContent: defence-in-depth — textContent
  // already escapes HTML, but if a future change uses innerHTML on this
  // node the esc() guarantees no markup leaks.
  logoEl.textContent = esc(opts.logo);
  const titleEl = d.createElement("div");
  titleEl.className = "pdf-title";
  titleEl.textContent = esc(opts.title);
  const metaEl = d.createElement("div");
  metaEl.className = "pdf-meta";
  const generated = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  metaEl.textContent = `Generated ${generated}`;
  headerInner.appendChild(logoEl);
  headerInner.appendChild(titleEl);
  headerInner.appendChild(metaEl);
  headerTop.appendChild(headerInner);
  header.appendChild(headerTop);

  // Q-04: only render the filters band when there are chips.
  if (opts.filterChips.length > 0) {
    const filters = d.createElement("div");
    filters.className = "pdf-filters";
    filters.setAttribute("data-testid", "pdf-filters");
    for (const chip of opts.filterChips) {
      const c = d.createElement("span");
      c.className = "pdf-filter-chip";
      c.textContent = esc(chip);
      filters.appendChild(c);
    }
    header.appendChild(filters);
  }

  d.body.appendChild(header);

  // Q-05: create the body container directly — no querySelector dependency.
  const body = d.createElement("div");
  body.className = "pdf-body";
  body.setAttribute("data-testid", "pdf-body");
  d.body.appendChild(body);
}

/** Clone the tab, strip controls, sanitise attributes, append to the
 * popup body. */
function _appendStrippedClone(win: Window, tabEl: HTMLElement, strip: string[]): void {
  const clone = tabEl.cloneNode(true) as HTMLElement;
  for (const sel of strip) {
    clone.querySelectorAll(sel).forEach((el) => el.remove());
  }
  // The AI Summary slot uses a runtime style attr in the live SPA to
  // size itself; that doesn't translate to a paginated print view.
  clone
    .querySelectorAll('[data-testid="ai-summary-slot"]')
    .forEach((el) => el.setAttribute("style", ""));

  // SEC-02: scrub event-handler attributes + url()-bearing style
  // attributes on every remaining node. The popup CSP already blocks
  // script execution, but this defence-in-depth prevents a future CSP
  // weakening from opening a script-injection path on its own.
  _scrubAttributes(clone);

  const body = win.document.querySelector('[data-testid="pdf-body"]');
  if (!body) {
    // Shouldn't happen — _buildPopupDocument creates it — but if some
    // browser extension mutates the popup between calls, fail loudly
    // rather than silently producing an empty PDF.
    throw new Error("[pdf_export] pdf-body container missing");
  }
  body.appendChild(win.document.adoptNode(clone));
}

/** Strip event-handler attributes (on*) and style="...url()..." from
 * every element in the subtree. SEC-02. */
function _scrubAttributes(root: HTMLElement): void {
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
  let node: Element | null = walker.currentNode as Element;
  while (node) {
    if (node instanceof Element) {
      for (const attr of Array.from(node.attributes)) {
        const name = attr.name.toLowerCase();
        if (name.startsWith("on")) {
          node.removeAttribute(attr.name);
          continue;
        }
        if (name === "style" && /url\s*\(|@import/i.test(attr.value)) {
          node.removeAttribute(attr.name);
        }
      }
    }
    node = walker.nextNode() as Element | null;
  }
}

const _STYLE_BLOCK = `
:root{--midnight:#0B111C;--ink:#0F172A;--slate:#475569;--line:#E5E7EB;--aqua:#2EEFEC;--coral:#FF4759;--purple:#853EFF}
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:"Inter",system-ui,-apple-system,sans-serif;color:var(--ink);background:#fff;padding:0;max-width:900px;margin:0 auto}
.pdf-header{background:var(--midnight);color:#fff;padding:24px 36px 20px;border-radius:0 0 12px 12px;margin-bottom:20px}
.pdf-header-top{display:flex;align-items:flex-start;justify-content:space-between;gap:12px}
.pdf-logo{font-family:"Space Grotesk","Inter",system-ui,sans-serif;font-size:12px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:rgba(255,255,255,.55);margin-bottom:8px}
.pdf-title{font-family:"Space Grotesk","Inter",system-ui,sans-serif;font-size:22px;font-weight:700;letter-spacing:-.02em;color:#fff}
.pdf-meta{font-size:11px;color:rgba(255,255,255,.55);margin-top:4px}
.pdf-filters{margin-top:14px;padding-top:14px;border-top:1px solid rgba(255,255,255,.12)}
.pdf-filter-chip{display:inline-block;padding:2px 10px;border-radius:12px;font-size:10px;font-weight:600;background:rgba(255,255,255,.12);color:rgba(255,255,255,.85);margin:0 4px 4px 0}
.pdf-body{padding:0 36px 36px}
.kpi-strip{display:flex;flex-wrap:wrap;gap:10px;margin-bottom:20px}
.kpi{border:1px solid var(--line);border-radius:8px;padding:10px 14px;min-width:110px}
.kpi-label{font-size:9px;text-transform:uppercase;letter-spacing:.08em;color:var(--slate);font-weight:600}
.kpi-value{font-family:"Space Grotesk","Inter",system-ui,sans-serif;font-size:17px;font-weight:700;color:var(--ink);font-variant-numeric:tabular-nums;margin-top:2px}
details{border:1px solid var(--line);border-radius:8px;margin:8px 0;overflow:hidden}
summary{padding:9px 14px;font-weight:600;font-size:13px;cursor:default;list-style:none;background:#FAFAFB;color:var(--ink)}
table{width:100%;border-collapse:collapse;font-size:11px;margin-top:6px}
th{text-align:left;padding:5px 10px;font-weight:600;color:var(--slate);border-bottom:2px solid var(--line);font-size:10px;text-transform:uppercase;letter-spacing:.05em}
td{padding:6px 10px;border-bottom:1px solid #F1F5F9;vertical-align:middle}
tr:hover td{background:#F8FAFC}
.chip{display:inline-flex;padding:1px 7px;border-radius:5px;font-size:10px;font-weight:600;background:#f1f5f9;color:#334155}
button,input,select{display:none!important}
@media print{@page{margin:12mm 10mm}.pdf-header{border-radius:0;margin-bottom:14px}details[open]{display:block}}
`.trim();

/** Map of date-range presets to human-readable labels. Exported so the
 * Reports view can share the same labels for its scope chips.
 *
 * Q-01 (PR #113 code review): when `range === 'custom'`, the active
 * window is whatever the user picked via the date inputs, not a fixed
 * preset. The caller passes `dateFrom` / `dateTo` so this helper can
 * format the custom window correctly; otherwise the PDF chip and the
 * KPI bar would disagree for the same export action.
 */
export function dateRangeLabel(
  range: string | null | undefined,
  year = new Date().getUTCFullYear(),
  dateFrom?: string | null,
  dateTo?: string | null,
): string {
  if (range === "custom") {
    if (dateFrom && dateTo) return `${dateFrom} → ${dateTo}`;
    if (dateFrom) return `from ${dateFrom}`;
    if (dateTo) return `until ${dateTo}`;
    return "Custom range";
  }
  const map: Record<string, string> = {
    ytd: `YTD ${year}`,
    cy0: `${year}`,
    "open-cy0": `Active ${year}+`,
    cy1: `${year - 1}`,
    cy2: `${year - 2}`,
    q1: `Q1 ${year}`,
    r12: "Rolling 12m",
    all: "All dates",
  };
  return map[range ?? "cy0"] ?? `${year}`;
}

/** Build a descriptive filename for an export.
 *
 * Pattern (from SGNL-179 AC #5):
 *   Signal-[ReportName]-[Scope]-[BU]-[Owner]-[from]-to-[to]
 *
 * Tokens with whitespace are dash-joined; empty/null tokens are dropped.
 * BU and Owner are slugified to `[a-zA-Z0-9._-]` so the filename is
 * shell-safe and Windows-safe (SEC-05 hardened BU to match Owner). */
export function buildExportFilename(opts: {
  reportName: string; // e.g. "Progress"
  scope: string; // e.g. "2026" / "Active 2026+"
  bu?: string | null;
  owner?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
}): string {
  const slugify = (s: string): string =>
    s.replace(/[^a-zA-Z0-9._-]/g, "-").replace(/-{2,}/g, "-").replace(/^-|-$/g, "");
  // PR-Agent (PRA-2, PR #113): reportName and scope are NOT pre-slugged
  // by callers — they're human-readable ("Pipeline Progress", "YTD 2026",
  // "Active 2026+"). Without slugifying them here, the filename would
  // carry whitespace, '+', and other shell/Windows-hazardous characters
  // despite the rest of this function carefully sanitising every other
  // token.
  const parts: string[] = [
    `Signal-${slugify(opts.reportName)}`,
    slugify(opts.scope),
  ].filter((s) => s && s !== "Signal-");
  if (opts.bu) {
    const slug = slugify(opts.bu);
    if (slug) parts.push(slug);
  }
  if (opts.owner) {
    const slug = slugify(opts.owner.split("@")[0]);
    if (slug) parts.push(slug);
  }
  // SEC-02: slugify dates too — picker inputs are operator-controllable
  // and an injected '../' shouldn't reach document.title.
  if (opts.fromDate) {
    const slug = slugify(opts.fromDate);
    if (slug) parts.push(slug);
  }
  if (opts.toDate) {
    const slug = slugify(opts.toDate);
    if (slug) parts.push("to", slug);
  }
  return parts.filter(Boolean).join("-");
}
